import * as React from 'react';

import { Badge, type BadgeProps } from '../primitives/badge';

/**
 * The "Tyndo Certified <Class>" badge (CS2-3 / CS3-1): renders a device's
 * device-interface conformance verdict at a glance. Backed by a
 * DeviceConformanceCertification record — conformant → a green certified mark with the
 * contract version; non-conformant → an amber gap; never-certified → muted; in-flight →
 * info. Class-agnostic (the contract names PLC/Vision/LIMS/… via `deviceClass`).
 */
export interface CertifiedPlcBadgeProps extends Omit<BadgeProps, 'children' | 'variant'> {
  /** Whether the device satisfied its class contract. */
  conformant: boolean;
  /** False when the device has no certification record yet. */
  hasCertification?: boolean;
  /** Device class name from the contract (e.g. "Plc"). */
  deviceClass?: string;
  /** Contract SemVer the device was certified against (e.g. "1.0.0"). */
  contractVersion?: string;
  /** True while the conformance check is loading. */
  isChecking?: boolean;
}

export const CertifiedPlcBadge = React.forwardRef<HTMLDivElement, CertifiedPlcBadgeProps>(
  ({ conformant, hasCertification = true, deviceClass, contractVersion, isChecking, ...props }, ref) => {
    let variant: BadgeProps['variant'];
    let label: string;
    if (isChecking) {
      variant = 'info';
      label = 'Checking conformance…';
    } else if (!hasCertification) {
      variant = 'muted';
      label = 'Not certified';
    } else if (conformant) {
      variant = 'success';
      label = `Tyndo Certified ${deviceClass || 'PLC'}`;
    } else {
      variant = 'warning';
      label = 'Conformance gap';
    }

    const certified = conformant && hasCertification && !isChecking;

    return (
      <Badge ref={ref} variant={variant} {...props}>
        <span aria-hidden>{certified ? '✓' : '•'}</span>
        <span>{label}</span>
        {certified && contractVersion ? <span className="opacity-70">v{contractVersion}</span> : null}
      </Badge>
    );
  }
);
CertifiedPlcBadge.displayName = 'CertifiedPlcBadge';
